import {
  bsc,
  mainnet,
  polygon,
  bscTestnet,
  hardhat,
  goerli,
  sepolia,
  optimism,
  baseSepolia,
  base,
  holesky,
  arbitrum,
} from 'viem/chains';
import { Chain } from '@wagmi/core/chains';
import { ValuesOfMap } from '../lib/utilityTypes';
import { defineChain } from 'viem';

export const CHAINS = {
  ETH_MAINNET: 1,
  ETH_HOLESKY: 17000,
  ETH_ROPSTEN: 3,
  ETH_GOERLI: 5,
  ETH_SEPOLIA: 11155111,
  ETH_KOVAN: 42,
  OPTIMISM_MAINNET: 10,
  OPTIMISM_GOERLI: 420,
  BASE_L2: 8453,
  BASE_SEPOLIA_L2: 84532,
  BSC_MAINNET: 56,
  BSC_TESTNET: 97,
  POLYGON_MAINNET: 137,
  SFI_TESTNET: 751,
  POLYGON_MUMBAI: 80001,
  HARDHAT_LOCAL_1: 31337,
  ETH_FORKED: 31438,
  BNB_FORKED: 31439,
  HARDHAT_LOCAL_3: 31339,
  HARDHAT_LOCAL_4: 31340,
  AVALANCHE_MAINNET: 43114,
  AVALANCHE_FUJI: 43113,
  ARBITRUM_ONE: 42161,
  ARBITRUM_RINKEBY: 421611, // Assuming 421611 is an Arbitrum testnet
  FANTOM_MAINNET: 250,
  FANTOM_TESTNET: 4002, // Assuming 4002 is Fantom testnet
  GETH_TESTNET: 1337,
  EMONEY_TESTNET: 4544,
} as const;

export type CardanoNetworksChainIds = 6 | 34;
export type NetworkChainIds =
  | ValuesOfMap<typeof CHAINS>
  | CardanoNetworksChainIds;

export interface NetworkProps extends Chain {
  id: NetworkChainIds;
  networkType?: 'L1' | 'L2';
  icon: string;
  iconUrl?: string;
  supported?: boolean;
  testnet: boolean;
  shortName?: string;
}
// export type NetworkName = typeof networksNames[number];

type NetworksProps = {
  [id: number]: NetworkProps;
};

export const ethRpcs = [
  'https://eth.blockrazor.xyz',
  'https://rpc.mevblocker.io',
  'https://eth1.lava.build',
  'https://ethereum-rpc.publicnode.com',
  'https://eth.drpc.org',
];

export const bscRpcs = [
  'https://bsc-dataseed1.binance.org',
  'https://bsc-rpc.publicnode.com',
  'https://bsc.drpc.org',
  'https://1rpc.io/bnb',
];

export const baseRpcs = [
  'https://base-rpc.publicnode.com',
  'https://base.drpc.org',
  'https://rpc.baseazul.dev',
  'https://gateway.tenderly.co/public/base',
  'https://rpc.nodeflare.app/base/public',
  'https://base.llamarpc.com',
  'https://1rpc.io/base',
];

export const polygonRpcs = [
  'https://api.zan.top/polygon-mainnet',
  'https://polygon.llamarpc.com',
  'https://1rpc.io/matic',
  'https://polygon-public.nodies.app',
  'https://polygon-mainnet.gateway.tatum.io',
];

export const optimismRpcs = [
  'https://mainnet.optimism.io',
  'https://optimism.rpc.subquery.network/public',
  'https://optimism-mainnet.gateway.tatum.io',
  'https://optimism-rpc.publicnode.com',
  'https://optimism.drpc.org',
];

export const arbitrumRpcs = [
  'https://arbitrum.drpc.org',
  'https://arb1.arbitrum.io/rpc',
  'https://api.zan.top/arb-one',
  'https://arbitrum.rpc.subquery.network/public',
  'https://arb1.lava.build',
];

const goerliRpcs = [
  'https://goerli.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161',
];

const sfiRpcs = ['https://rpc-testnet.singularityfinance.ai'];

export const sfiTestnet = defineChain({
  id: CHAINS.SFI_TESTNET,
  name: 'SingularityFinance Testnet',
  network: 'L1',
  nativeCurrency: {
    decimals: 18,
    name: 'SFI',
    symbol: 'SFI',
  },
  rpcUrls: {
    default: {
      http: sfiRpcs,
    },
    public: {
      http: sfiRpcs,
    },
  },
  blockExplorers: {
    default: {
      name: 'Explorer',
      url: 'https://explorer-testnet.singularityfinance.ai/',
    },
  },
  testnet: true,
});

export const networks: NetworksProps = {
  [CHAINS.ETH_MAINNET]: {
    ...mainnet,
    shortName: 'ETH',
    icon: 'mingcute:ethereum-line',
    iconUrl: '/icons/networks/ETHEREUM.svg',
    networkType: 'L1',
    supported: true,
    testnet: false,
    rpcUrls: {
      default: {
        http: ethRpcs,
      },
      public: {
        http: ethRpcs,
      },
    },
  },
  5: {
    ...goerli,
    shortName: 'Goerli',
    icon: 'mingcute:ethereum-line',
    networkType: 'L1',
    supported: false,
    testnet: true,
    rpcUrls: {
      default: {
        http: goerliRpcs,
      },
      public: {
        http: goerliRpcs,
      },
    },
  },
  11155111: {
    ...sepolia,
    rpcUrls: {
      alchemy: {
        http: ['https://eth-sepolia.g.alchemy.com/v2'],
        webSocket: ['wss://eth-sepolia.g.alchemy.com/v2'],
      },
      infura: {
        http: ['https://sepolia.infura.io/v3'],
        webSocket: ['wss://sepolia.infura.io/ws/v3'],
      },
      default: {
        http: ['https://1rpc.io/sepolia'],
      },
      public: {
        http: ['https://1rpc.io/sepolia'],
      },
    },
    shortName: 'Sepolia',
    icon: 'mingcute:ethereum-line',
    networkType: 'L1',
    supported: true,
    testnet: true,
  },
  [CHAINS.BSC_MAINNET]: {
    ...bsc,
    networkType: 'L1',
    icon: 'mingcute:binance-coin-bnb-fill',
    iconUrl: '/icons/networks/BSC.svg',
    supported: true,
    testnet: false,
    shortName: 'BSC',
    rpcUrls: {
      default: {
        http: bscRpcs,
      },
      public: {
        http: bscRpcs,
      },
    },
  },
  [CHAINS.BSC_TESTNET]: {
    ...bscTestnet,
    networkType: 'L1',
    icon: 'mingcute:binance-coin-bnb-fill',
    supported: true,
    testnet: true,
    shortName: 'BSCt',
  },
  17000: {
    ...holesky,
    networkType: 'L1',
    icon: 'mingcute:ethereum-line',
    shortName: 'Holesky',
    supported: false,
    testnet: true,
  },
  10: {
    ...optimism,
    iconUrl: '/icons/networks/OPTIMISM.svg',
    networkType: 'L2',
    icon: 'simple-icons:optimism',
    supported: true,
    testnet: false,
    shortName: 'OPT',
    rpcUrls: {
      default: {
        http: optimismRpcs,
      },
      public: {
        http: optimismRpcs,
      },
    },
  },
  [CHAINS.BASE_L2]: {
    ...base,
    iconUrl: '/icons/networks/BASE.svg',
    networkType: 'L2',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    icon: 'token:base',
    shortName: 'BASE',
    rpcUrls: {
      default: {
        http: baseRpcs,
      },
      public: {
        http: baseRpcs,
      },
    },
    supported: true,
    testnet: false,
  },
  84532: {
    ...baseSepolia,
    networkType: 'L2',
    nativeCurrency: { name: 'Ether', symbol: 'BASE', decimals: 18 },
    icon: 'token:base',
    shortName: 'tBASE',
    supported: true,
    testnet: true,
  },
  137: {
    ...polygon,
    iconUrl: '/icons/networks/POLYGON.svg',
    networkType: 'L2',
    shortName: 'Polygon',
    icon: 'cryptocurrency:matic',
    supported: true,
    testnet: false,
    rpcUrls: {
      default: {
        http: polygonRpcs,
      },
      public: {
        http: polygonRpcs,
      },
    },
  },
  42161: {
    ...arbitrum,
    iconUrl: '/icons/networks/ARBITRUM.svg',
    networkType: 'L2',
    shortName: 'Arbitrum',
    icon: 'cryptocurrency:matic',
    supported: true,
    testnet: false,
    rpcUrls: {
      default: {
        http: arbitrumRpcs,
      },
      public: {
        http: arbitrumRpcs,
      },
    },
  },
  34: {
    id: 34,
    name: 'Cardano',
    network: 'Cardano',
    nativeCurrency: {
      name: 'ADA',
      symbol: 'ADA',
      decimals: 34,
    },
    icon: 'mingcute:cardano-ada-line',
    blockExplorers: {
      default: { name: '', url: 'https://cardanoscan.io/' },
      etherscan: { name: '', url: '' },
    },
    rpcUrls: {
      default: { http: [`https://rpc-mumbai.matic.today`] },
      public: { http: [`https://rpc-mumbai.matic.today`] },
    },
    supported: false,
    testnet: false,
  },
  6: {
    id: 6,
    name: 'CardanoPreprod',
    network: 'Cardano',
    nativeCurrency: {
      name: 'ADA',
      symbol: 'ADA',
      decimals: 6,
    },
    icon: 'mingcute:cardano-ada-line',
    blockExplorers: {
      default: { name: '', url: 'https://preprod.cardanoscan.io/' },
      etherscan: { name: '', url: '' },
    },
    rpcUrls: {
      default: { http: [`https://preprod.cardanoscan.io/`] },
      public: { http: [`https://preprod.cardanoscan.io/`] },
    },
    supported: false,
    testnet: true,
  },
  31337: {
    ...hardhat,
    icon: 'icon-park-outline:magic-hat',
    supported: true,
    testnet: true,
  },
  [CHAINS.ETH_FORKED]: {
    id: CHAINS.ETH_FORKED,
    name: 'Forked ETH',
    iconUrl: '/icons/networks/ETHEREUM.svg',
    network: 'ForkedETH',
    shortName: 'fETH',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    icon: 'icon-park-outline:magic-hat',
    supported: true,
    testnet: true,
    blockExplorers: {
      default: {
        name: 'Etherscan',
        url: 'https://etherscan.io',
      },
    },
    rpcUrls: {
      default: {
        http: ['https://forked-eth.dev.singularitydao.ai'],
      },
      public: {
        http: ['https://forked-eth.dev.singularitydao.ai'],
      },
    },
  },
  [CHAINS.BNB_FORKED]: {
    id: CHAINS.BNB_FORKED,
    name: 'Forked BNB',
    network: 'ForkedBNB',
    shortName: 'fBNB',
    nativeCurrency: {
      name: 'Binance coin',
      symbol: 'BNB',
      decimals: 18,
    },
    icon: 'icon-park-outline:magic-hat',
    supported: true,
    testnet: true,
    rpcUrls: {
      default: {
        http: ['https://forked-bsc.dev.singularitydao.ai'],
      },
      public: {
        http: ['https://forked-bsc.dev.singularitydao.ai'],
      },
    },
  },
  31340: {
    id: 31340,
    name: 'Forked Matic',
    network: 'ForkedMatic',
    shortName: 'fMAT',
    nativeCurrency: {
      name: 'MATIC',
      symbol: 'MAT',
      decimals: 18,
    },
    icon: 'icon-park-outline:magic-hat',
    supported: false,
    testnet: true,
    rpcUrls: {
      default: {
        http: [
          'https://7xusx271ab.execute-api.eu-west-2.amazonaws.com/beta/rpc',
        ],
      },
      public: {
        http: [
          'https://7xusx271ab.execute-api.eu-west-2.amazonaws.com/beta/rpc',
        ],
      },
    },
  },
  [CHAINS.SFI_TESTNET]: {
    id: CHAINS.SFI_TESTNET,
    name: 'SFI Testnet',
    network: 'SFI Testnet',
    shortName: 'SFITestnet',
    networkType: 'L2',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    icon: 'icon-park-outline:magic-hat',
    supported: true,
    testnet: false,
    blockExplorers: {
      default: {
        name: 'SingularityFinance Testnet explorer',
        url: 'https://explorer-testnet.singularityfinance.ai',
      },
    },
    rpcUrls: {
      default: {
        http: ['https://rpc-testnet.singularityfinance.ai'],
      },
      public: {
        http: ['https://rpc-testnet.singularityfinance.ai'],
      },
    },
  },
} as NetworksProps;

export const enabledNetworks: readonly [NetworkProps, ...NetworkProps[]] =
  Object.values(networks).filter(
    (network) =>
      network.supported &&
      (process.env.NEXT_PUBLIC_NODE_ENV === 'production'
        ? network.testnet === false
        : network.testnet === true || network.testnet === false)
  ) as unknown as [NetworkProps, ...NetworkProps[]];

export const defaultNetwork = networks[mainnet.id];

export function getNetworkFromChainId(chainId: NetworkChainIds): NetworkProps {
  return networks[chainId];
}

export const supportedNetworksByChainId: NetworksProps = Object.fromEntries(
  Object.entries(networks)
    .filter(([_, network]) => network.supported)
    .map(([key, network]) => [network.id, network])
) as { [key in NetworkChainIds]: NetworkProps };

export function isCardanoChain(chainId: NetworkChainIds): boolean {
  return (
    chainId === (34 as NetworkChainIds) || chainId === (6 as NetworkChainIds)
  );
}

export const enabledNetworksChainIds: NetworkChainIds[] = Object.values(
  enabledNetworks
).map((network) => network.id);

export function getNetworkByName(name: string): NetworkProps | undefined {
  const networkIds = Object.keys(networks);
  for (let i = 0; i < networkIds.length; i++) {
    const networkId = parseInt(networkIds[i]);
    const network = networks[networkId];
    if (network.name === name) {
      return network;
    }
  }
  return undefined;
}

export function getExplorerLink(
  chainId: number,
  address: string,
  type: 'TXN' | 'ADDRESS' = 'ADDRESS'
): string {
  const network = getNetworkFromChainId(chainId as NetworkChainIds);

  if (!network || !network.blockExplorers) {
    return '';
  }

  if (type === 'TXN' && network.blockExplorers.default.url) {
    return `${network.blockExplorers.default.url}/tx/${address}`;
  }

  if (network.blockExplorers.default.url) {
    return `${network.blockExplorers.default.url}/address/${address}`;
  }

  return '';
}

export function getNetworkByChainId(chainId: number): NetworkProps | undefined {
  return networks[chainId];
}

export function getSupportedNetworkFromChainId(
  chainId: NetworkChainIds
): NetworkProps | undefined {
  const network = networks[chainId];
  if (network && network.supported) {
    return network;
  }
  return undefined;
}
